import { asRec, isRec, listaDe, pick, sinDuplicados, toFecha, toNum, toStr } from './adaptHelpers';
import { compareISO } from './calendar';
import { fuenteLabel } from './labels';
import { calidadDeNota } from './notas';
import { roundTo } from './numbers';
import {
  filaHistoricoSchema,
  filaPrecioSchema,
  fuenteSchema,
  tasaBcvSchema,
  type FilaHistoricoCruda,
  type FilaPrecioCruda,
} from './schemas';
import type {
  Fuente,
  Historico,
  Precio,
  PuntoHistorico,
  RubroInfo,
  TasaBcv,
  UltimosData,
} from './types';

export * from './adaptersAnalitica';
export * from './adaptersCuenta';

/**
 * Adaptadores de la API de MERCADO (docs/CONTRATO_API.md §4). Convierten cualquier forma razonable en los
 * tipos de dominio, descartan las filas inválidas sin romper el resto y solo LANZAN cuando la respuesta
 * entera no sirve (los servicios lo traducen a ApiError 'respuesta_invalida').
 */
export interface Adaptado<T> {
  data: T;
  /** Filas descartadas por inválidas (o duplicadas). */
  descartadas: number;
}

const noNulo = <T>(v: T | null): v is T => v !== null;

/* ───────────── Filas de precio ───────────── */

interface Rango {
  min: number;
  max: number;
  prom: number;
  invertido: boolean;
}

type PreciosCrudos = Pick<FilaPrecioCruda, 'precio_min' | 'precio_max' | 'precio_prom'>;

function normalizarRango(f: PreciosCrudos): Rango {
  const min = f.precio_min ?? f.precio_prom ?? f.precio_max ?? 0;
  const max = f.precio_max ?? f.precio_prom ?? f.precio_min ?? 0;
  const invertido = min > max;
  const [lo, hi] = invertido ? [max, min] : [min, max];
  return { min: lo, max: hi, prom: f.precio_prom ?? roundTo((lo + hi) / 2, 4), invertido };
}

interface Bolivares {
  tasaBcv: number | null;
  minBs: number | null;
  maxBs: number | null;
  promBs: number | null;
}

type BsCrudos = Pick<FilaPrecioCruda, 'tasa_bcv' | 'min_bs' | 'max_bs' | 'prom_bs'>;

function normalizarBs(f: BsCrudos): Bolivares {
  const a = f.min_bs ?? f.max_bs ?? f.prom_bs;
  const b = f.max_bs ?? f.min_bs ?? f.prom_bs;
  const [minBs, maxBs] = a !== null && b !== null && a > b ? [b, a] : [a, b];
  const promBs =
    f.prom_bs ?? (minBs !== null && maxBs !== null ? roundTo((minBs + maxBs) / 2, 4) : null);
  return {
    tasaBcv: f.tasa_bcv !== null && f.tasa_bcv > 0 ? f.tasa_bcv : null,
    minBs,
    maxBs,
    promBs,
  };
}

const finDeFila = (c: { fecha: string; fecha_fin: string | null }): string | null =>
  c.fecha_fin !== null && c.fecha_fin >= c.fecha ? c.fecha_fin : null;

function construirPrecio(c: FilaPrecioCruda): Precio {
  const rango = normalizarRango(c);
  const calidad = calidadDeNota(c.nota);
  if (rango.invertido) calidad.avisos.push('El mínimo y el máximo venían invertidos');
  return {
    id: `${c.fuente}|${c.fecha}|${c.rubro}|${c.categoria}|${c.presentacion ?? ''}|${c.region ?? ''}`,
    rubro: c.rubro,
    categoria: c.categoria,
    presentacion: c.presentacion,
    unidad: c.unidad ?? 'kg',
    moneda: (c.moneda ?? 'USD').toUpperCase(),
    min: rango.min,
    max: rango.max,
    prom: rango.prom,
    ...normalizarBs(c),
    region: c.region,
    fecha: c.fecha,
    fechaFin: finDeFila(c),
    fuente: c.fuente,
    nota: c.nota,
    calidad,
  };
}

/** Una fila cruda → Precio, o null si no sirve (sin rubro, fecha válida o precio). */
export function adaptPrecio(raw: unknown): Precio | null {
  const r = filaPrecioSchema.safeParse(raw);
  return r.success ? construirPrecio(r.data) : null;
}

/* ───────────── /v1/precios/ultimos ───────────── */

const CLAVES_FILAS = ['precios', 'data', 'items', 'resultados'] as const;

export function adaptUltimosConConteo(raw: unknown): Adaptado<UltimosData> {
  const filas = listaDe(raw, CLAVES_FILAS);
  const rec = asRec(raw);
  const tasa = toNum(pick(rec, 'tasa_bcv_actual', 'tasaBcvActual', 'tasa_bcv', 'tasa'));
  const fecha = toFecha(pick(rec, 'tasa_bcv_fecha', 'tasaBcvFecha', 'fecha_tasa', 'fecha'));
  if (!filas) throw new Error('ultimos: la respuesta no trae una lista de precios');
  if (tasa === null || tasa <= 0) throw new Error('ultimos: falta una tasa BCV válida');
  if (fecha === null) throw new Error('ultimos: falta la fecha de la tasa BCV');
  const precios = sinDuplicados(filas.map(adaptPrecio).filter(noNulo), (p) => p.id);
  if (filas.length > 0 && precios.length === 0) throw new Error('ultimos: ninguna fila es válida');
  return {
    data: { tasaBcvActual: tasa, tasaBcvFecha: fecha, total: precios.length, precios },
    descartadas: filas.length - precios.length,
  };
}

export const adaptUltimos = (raw: unknown): UltimosData => adaptUltimosConConteo(raw).data;

/* ───────────── /v1/precios/historico ───────────── */

/** Qué serie se pidió: sirve para rellenar lo que las filas omiten y para elegir entre series mezcladas. */
export interface HistoricoCtx {
  rubro?: string;
  categoria?: string;
  presentacion?: string | null;
}

const CLAVES_HISTORICO = [
  'precios',
  'serie',
  'datos',
  'data',
  'items',
  'resultados',
  'historico',
  'puntos',
] as const;

function construirPunto(c: FilaHistoricoCruda): PuntoHistorico {
  const precio = construirPrecio({
    ...c,
    rubro: c.rubro ?? '',
    categoria: c.categoria ?? '',
    fuente: c.fuente ?? '',
  });
  return {
    fecha: precio.fecha,
    fechaFin: precio.fechaFin,
    min: precio.min,
    max: precio.max,
    prom: precio.prom,
    tasaBcv: precio.tasaBcv,
    minBs: precio.minBs,
    maxBs: precio.maxBs,
    promBs: precio.promBs,
    region: precio.region,
    fuente: c.fuente,
    nota: precio.nota,
    calidad: precio.calidad,
  };
}

const ordenPuntos = (a: PuntoHistorico, b: PuntoHistorico): number =>
  compareISO(a.fecha, b.fecha) ||
  compareISO(a.fechaFin ?? a.fecha, b.fechaFin ?? b.fecha) ||
  (a.fuente ?? '').localeCompare(b.fuente ?? '') ||
  (a.region ?? '').localeCompare(b.region ?? '');

const ordenarPuntos = (puntos: PuntoHistorico[]): PuntoHistorico[] =>
  sinDuplicados(
    [...puntos].sort(ordenPuntos),
    (p) => `${p.fuente ?? ''}|${p.fecha}|${p.region ?? ''}`,
  );

/** Agrupa las filas en una serie por (rubro, categoría, presentación). Cada una queda ordenada y sin duplicados. */
export function adaptHistoricoSeries(raw: unknown, ctx: HistoricoCtx = {}): Adaptado<Historico[]> {
  const filas = listaDe(raw, CLAVES_HISTORICO);
  if (!filas) throw new Error('historico: la respuesta no trae una lista de puntos');
  const grupos = new Map<string, { base: Historico; filas: FilaHistoricoCruda[] }>();
  let validas = 0;
  for (const fila of filas) {
    const r = filaHistoricoSchema.safeParse(fila);
    if (!r.success) continue;
    validas += 1;
    const rubro = r.data.rubro ?? ctx.rubro ?? '';
    const categoria = r.data.categoria ?? ctx.categoria ?? null;
    const presentacion = r.data.presentacion ?? ctx.presentacion ?? null;
    const clave = `${rubro}|${categoria ?? ''}|${presentacion ?? ''}`;
    const grupo = grupos.get(clave) ?? {
      base: { rubro, categoria, presentacion, unidad: r.data.unidad, puntos: [] },
      filas: [],
    };
    grupo.filas.push(r.data);
    grupos.set(clave, grupo);
  }
  if (filas.length > 0 && validas === 0) throw new Error('historico: ningún punto es válido');
  const series = [...grupos.values()].map((g) => ({
    ...g.base,
    puntos: ordenarPuntos(g.filas.map(construirPunto)),
  }));
  return { data: series, descartadas: filas.length - validas };
}

const unir = (series: Historico[], ctx: HistoricoCtx): Historico => {
  const comun = <K extends 'categoria' | 'presentacion'>(campo: K): Historico[K] | null => {
    const valores = new Set(series.map((s) => s[campo]));
    return valores.size === 1 ? ([...valores][0] ?? null) : null;
  };
  return {
    rubro: series[0]?.rubro ?? ctx.rubro ?? '',
    categoria: comun('categoria'),
    presentacion: comun('presentacion'),
    unidad: series.find((s) => s.unidad)?.unidad ?? null,
    puntos: ordenarPuntos(series.flatMap((s) => s.puntos)),
  };
};

/**
 * Una sola serie: la pedida en `ctx` (categoría y presentación) si hay varias mezcladas; si no se fijó
 * ninguna y hay varias, se unen (categoría/presentación quedan null = "varias").
 */
export function adaptHistorico(raw: unknown, ctx: HistoricoCtx = {}): Historico {
  const { data } = adaptHistoricoSeries(raw, ctx);
  let series = data;
  if (ctx.categoria !== undefined) series = series.filter((s) => s.categoria === ctx.categoria);
  if (ctx.presentacion !== undefined)
    series = series.filter((s) => s.presentacion === ctx.presentacion);
  const unica = series.length === 1 ? series[0] : undefined;
  if (unica) return unica;
  if (series.length === 0) {
    return {
      rubro: ctx.rubro ?? '',
      categoria: ctx.categoria ?? null,
      presentacion: ctx.presentacion ?? null,
      unidad: null,
      puntos: [],
    };
  }
  return unir(series, ctx);
}

/* ───────────── /v1/rubros ───────────── */

const textoDe = (v: unknown): string | null =>
  isRec(v) ? toStr(pick(v, 'categoria', 'nombre', 'clave', 'slug')) : toStr(v);

function categoriasDe(v: unknown): string[] {
  const lista = Array.isArray(v)
    ? v
    : isRec(v)
      ? (listaDe(v, ['categorias', 'categories']) ?? [])
      : [];
  return lista.map(textoDe).filter(noNulo);
}

function entradasDeRubros(raw: unknown): Array<[string, string[]]> | null {
  const lista = listaDe(raw, ['rubros', 'data', 'items', 'resultados']);
  if (lista) {
    return lista.flatMap((item): Array<[string, string[]]> => {
      if (!isRec(item)) return toStr(item) ? [[toStr(item) as string, []]] : [];
      const rubro = toStr(pick(item, 'rubro', 'nombre', 'clave'));
      if (!rubro) return [];
      const unica = toStr(item.categoria);
      return [[rubro, unica ? [unica] : categoriasDe(item.categorias ?? item.categories)]];
    });
  }
  if (!isRec(raw)) return null;
  const valores = Object.values(raw);
  if (valores.length === 0 || !valores.every((v) => Array.isArray(v) || isRec(v))) return null;
  return Object.entries(raw).map(([rubro, v]) => [rubro, categoriasDe(v)]);
}

export function adaptRubros(raw: unknown): RubroInfo[] {
  const entradas = entradasDeRubros(raw);
  if (!entradas) throw new Error('rubros: forma no reconocida');
  const porRubro = new Map<string, string[]>();
  for (const [rubro, categorias] of entradas) {
    porRubro.set(rubro, [...new Set([...(porRubro.get(rubro) ?? []), ...categorias])]);
  }
  return [...porRubro].map(([rubro, categorias]) => ({ rubro, categorias }));
}

/* ───────────── /v1/fuentes ───────────── */

function fuenteDe(raw: unknown, claveImplicita?: string): Fuente | null {
  const r = fuenteSchema.safeParse(
    isRec(raw) && claveImplicita ? { fuente: claveImplicita, ...raw } : raw,
  );
  if (!r.success) return null;
  const clave = r.data.fuente ?? r.data.nombre?.toLowerCase().replace(/\s+/g, '-') ?? null;
  if (!clave) return null;
  const { nombre, tipo, url, region, frecuencia, permiso, ultimo_dato: ultimoDato } = r.data;
  return {
    clave,
    nombre: nombre ?? fuenteLabel(clave),
    tipo,
    url,
    region,
    frecuencia,
    permiso,
    ultimoDato,
  };
}

export function adaptFuentes(raw: unknown): Fuente[] {
  const lista = listaDe(raw, ['fuentes', 'data', 'items', 'resultados']);
  let fuentes: Fuente[];
  if (lista) fuentes = lista.map((f) => fuenteDe(f)).filter(noNulo);
  else if (isRec(raw) && Object.values(raw).length > 0 && Object.values(raw).every(isRec)) {
    fuentes = Object.entries(raw)
      .map(([clave, f]) => fuenteDe(f, clave))
      .filter(noNulo);
  } else throw new Error('fuentes: forma no reconocida');
  return sinDuplicados(fuentes, (f) => f.clave);
}

/* ───────────── /v1/bcv ───────────── */

const CLAVES_TASAS = ['tasas', 'historico', 'data', 'items', 'resultados', 'serie', 'bcv'] as const;

export function adaptBcvHistorico(raw: unknown): TasaBcv[] {
  const lista = listaDe(raw, CLAVES_TASAS);
  if (!lista) throw new Error('bcv: la respuesta no trae una lista de tasas');
  const tasas: TasaBcv[] = [];
  for (const item of lista) {
    const r = tasaBcvSchema.safeParse(item);
    if (r.success)
      tasas.push({ fecha: r.data.fecha, usd: r.data.usd, eur: r.data.eur, fuente: r.data.fuente });
  }
  if (lista.length > 0 && tasas.length === 0) throw new Error('bcv: ninguna tasa es válida');
  tasas.sort((a, b) => compareISO(a.fecha, b.fecha));
  return sinDuplicados(tasas.reverse(), (t) => t.fecha).reverse();
}

/** Una tasa: objeto `{fecha, usd}` (también `tasa|valor|dolar`), envuelto en `{tasa|data|bcv}`, o la más reciente de una lista. */
export function adaptBcv(raw: unknown): TasaBcv {
  const envuelto = isRec(raw) ? pick(raw, 'tasa', 'data', 'bcv') : undefined;
  const interior = isRec(envuelto) ? envuelto : raw;
  const directa = tasaBcvSchema.safeParse(interior);
  if (directa.success) {
    const { fecha, usd, eur, fuente } = directa.data;
    return { fecha, usd, eur, fuente };
  }
  const ultima = adaptBcvHistorico(raw).at(-1);
  if (!ultima) throw new Error('bcv: sin tasas');
  return ultima;
}
